import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MdGrid3X3 } from 'react-icons/md';

import TicTacToeBoard from './components/TicTacToeBoard';
import TicTacToeControls from './components/TicTacToeControls';
import TicTacToeScoreboard from './components/TicTacToeScoreboard';
import TicTacToeMoveHistory from './components/TicTacToeMoveHistory';
import { emptyBoard, checkWinner, isBoardFull, applyMove } from './utils/gameLogic';
import { pickAiMove } from './utils/ai';
import { loadScores, saveScores, loadPrefs, savePrefs } from './utils/storage';
import { encodeGameParams, decodeGameParams } from './utils/urlState';
import { playDrawSound, playMoveSound, playWinSound, resumeAudio } from './utils/sounds';
import { sendEvent } from '../../common/analytics';

const AI_PLAYER = 'O';
const HUMAN_PLAYER = 'X';

function countMarks(board) {
  let x = 0;
  let o = 0;
  for (const c of board) {
    if (c === 'X') x += 1;
    else if (c === 'O') o += 1;
  }
  return { x, o };
}

/** Next player from mark counts (X always starts). */
function inferTurn(board) {
  const { x, o } = countMarks(board);
  return x === o ? 'X' : 'O';
}

const TicTacToe = () => {
  const [, setSearchParams] = useSearchParams();
  const urlSyncReadyRef = useRef(false);
  const didDecodeUrlRef = useRef(false);

  const [mode, setMode] = useState(() => loadPrefs().mode);
  const [difficulty, setDifficulty] = useState(() => loadPrefs().difficulty);
  const [soundEnabled, setSoundEnabled] = useState(() => loadPrefs().sound);

  const [scores, setScores] = useState(() => loadScores());
  const [past, setPast] = useState(() => []);

  const [board, setBoard] = useState(() => emptyBoard());
  const [currentPlayer, setCurrentPlayer] = useState(() => /** @type {'X' | 'O'} */ ('X'));
  const [winner, setWinner] = useState(() => /** @type {'X' | 'O' | 'draw' | null} */ (null));
  const [winningLine, setWinningLine] = useState(() => /** @type {number[] | null} */ (null));
  const [moveLog, setMoveLog] = useState(() => /** @type {{ player: 'X' | 'O', index: number }[]} */ ([]));

  const [aiThinking, setAiThinking] = useState(false);
  const aiRunRef = useRef(0);

  const gameOver = winner !== null;

  /** Hydrate from URL once before paint so URL sync sees the restored board */
  useLayoutEffect(() => {
    if (didDecodeUrlRef.current) return;
    didDecodeUrlRef.current = true;
    const decoded = decodeGameParams(new URLSearchParams(window.location.search));
    if (!decoded || !decoded.board) {
      urlSyncReadyRef.current = true;
      return;
    }

    const ob = decoded.board;
    const oc = decoded.currentPlayer;
    const om = decoded.mode;
    const od = decoded.difficulty;

    const w = checkWinner(ob);
    const full = isBoardFull(ob);
    if (w || full) {
      urlSyncReadyRef.current = true;
      return;
    }

    const turn = oc && (oc === 'X' || oc === 'O') ? oc : inferTurn(ob);
    const { x, o } = countMarks(ob);
    const validTurn =
      (turn === 'X' && x === o) || (turn === 'O' && x === o + 1);
    if (!validTurn) {
      urlSyncReadyRef.current = true;
      return;
    }

    setBoard(ob);
    setCurrentPlayer(turn);
    if (om) setMode(om);
    if (od) setDifficulty(od);
    setWinner(null);
    setWinningLine(null);
    setMoveLog([]);
    setPast([]);
    urlSyncReadyRef.current = true;
  }, []);

  /** Sync URL when game state changes (no query on empty board) */
  useEffect(() => {
    if (!urlSyncReadyRef.current) return;
    const next = encodeGameParams({
      board,
      currentPlayer,
      mode,
      difficulty,
      gameOver,
    });
    const qs = new URLSearchParams(next).toString();
    const cur = window.location.search.replace(/^\?/, '');
    if (qs === cur) return;
    setSearchParams(next, { replace: true });
  }, [board, currentPlayer, mode, difficulty, gameOver, setSearchParams]);

  const persistPrefs = useCallback((next) => {
    savePrefs(next);
  }, []);

  const handleModeChange = useCallback(
    (m) => {
      setMode(m);
      persistPrefs({ mode: m });
      setPast([]);
      setBoard(emptyBoard());
      setCurrentPlayer('X');
      setWinner(null);
      setWinningLine(null);
      setMoveLog([]);
      setAiThinking(false);
      sendEvent('TicTacToe', 'Mode', m);
    },
    [persistPrefs]
  );

  const handleDifficultyChange = useCallback(
    (d) => {
      setDifficulty(d);
      persistPrefs({ difficulty: d });
      sendEvent('TicTacToe', 'Difficulty', d);
    },
    [persistPrefs]
  );

  const handleSoundToggle = useCallback(
    (v) => {
      setSoundEnabled(v);
      persistPrefs({ sound: v });
      if (v) resumeAudio();
    },
    [persistPrefs]
  );

  const finalizeResult = useCallback(
    (result, line) => {
      setWinner(result);
      setWinningLine(line ?? null);
      setScores((prev) => {
        const n = { ...prev };
        if (result === 'X') n.x += 1;
        else if (result === 'O') n.o += 1;
        else if (result === 'draw') n.draws += 1;
        saveScores(n);
        return n;
      });
      if (soundEnabled) {
        if (result === 'draw') playDrawSound();
        else playWinSound();
      }
    },
    [soundEnabled]
  );

  const playPly = useCallback(
    (index, player) => {
      if (board[index] !== null || winner !== null) return;

      setPast((p) => [
        ...p,
        {
          board: [...board],
          currentPlayer,
          winner,
          winningLine,
          moveLog: [...moveLog],
        },
      ]);

      const nextBoard = applyMove(board, index, player);
      setBoard(nextBoard);
      setMoveLog((log) => [...log, { player, index }]);

      const w = checkWinner(nextBoard);
      if (w) {
        finalizeResult(w.winner, w.line);
        return;
      }
      if (isBoardFull(nextBoard)) {
        finalizeResult('draw', null);
        return;
      }

      const next = player === 'X' ? 'O' : 'X';
      setCurrentPlayer(next);
      if (soundEnabled) playMoveSound();
    },
    [board, currentPlayer, winner, winningLine, moveLog, finalizeResult, soundEnabled]
  );

  const onCellClick = useCallback(
    (index) => {
      if (gameOver || board[index] !== null || aiThinking) return;
      if (mode === 'ai') {
        if (currentPlayer !== HUMAN_PLAYER) return;
      }
      playPly(index, currentPlayer);
    },
    [gameOver, board, aiThinking, mode, currentPlayer, playPly]
  );

  /** Keys 1–9 map to board cells (top-left → bottom-right). */
  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const n = e.key;
      if (n < '1' || n > '9') return;
      e.preventDefault();
      const idx = Number(n) - 1;
      onCellClick(idx);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCellClick]);

  /** AI move */
  useEffect(() => {
    if (mode !== 'ai' || gameOver || currentPlayer !== AI_PLAYER) {
      setAiThinking(false);
      return;
    }

    const runId = ++aiRunRef.current;
    setAiThinking(true);

    const delay = difficulty === 'easy' ? 45 : difficulty === 'medium' ? 85 : 55;
    const t = window.setTimeout(() => {
      if (aiRunRef.current !== runId) return;
      const move = pickAiMove(board, AI_PLAYER, difficulty);
      if (move === null) {
        setAiThinking(false);
        return;
      }

      setPast((p) => [
        ...p,
        {
          board: [...board],
          currentPlayer,
          winner,
          winningLine,
          moveLog: [...moveLog],
        },
      ]);

      const nextBoard = applyMove(board, move, AI_PLAYER);
      setBoard(nextBoard);
      setMoveLog((log) => [...log, { player: AI_PLAYER, index: move }]);

      const w = checkWinner(nextBoard);
      if (w) {
        finalizeResult(w.winner, w.line);
        setAiThinking(false);
        return;
      }
      if (isBoardFull(nextBoard)) {
        finalizeResult('draw', null);
        setAiThinking(false);
        return;
      }

      setCurrentPlayer(HUMAN_PLAYER);
      if (soundEnabled) playMoveSound();
      setAiThinking(false);
    }, delay);

    return () => {
      window.clearTimeout(t);
    };
  }, [
    mode,
    gameOver,
    currentPlayer,
    board,
    difficulty,
    winner,
    winningLine,
    moveLog,
    finalizeResult,
    soundEnabled,
  ]);

  const resetGame = useCallback(() => {
    setPast([]);
    setBoard(emptyBoard());
    setCurrentPlayer('X');
    setWinner(null);
    setWinningLine(null);
    setMoveLog([]);
    setAiThinking(false);
    sendEvent('TicTacToe', 'Reset', 'new-game');
  }, []);

  const undo = useCallback(() => {
    if (!past.length) return;
    const snap = past[past.length - 1];
    setPast((p) => p.slice(0, -1));
    setBoard(snap.board);
    setCurrentPlayer(snap.currentPlayer);
    setWinner(snap.winner);
    setWinningLine(snap.winningLine);
    setMoveLog(snap.moveLog);
    setAiThinking(false);
    sendEvent('TicTacToe', 'Undo', '1');
  }, [past]);

  const statusText = useMemo(() => {
    if (winner === 'draw') return 'Draw';
    if (winner === 'X') return 'X wins';
    if (winner === 'O') return 'O wins';
    if (mode === 'ai' && currentPlayer === AI_PLAYER) {
      return aiThinking ? 'AI is thinking…' : 'AI to move';
    }
    if (mode === 'ai' && currentPlayer === HUMAN_PLAYER) return 'Your turn';
    if (mode === 'pvp' && currentPlayer === 'X') return "X's turn";
    if (mode === 'pvp' && currentPlayer === 'O') return "O's turn";
    return 'Ready to play';
  }, [winner, mode, currentPlayer, aiThinking]);

  const statusSubline = useMemo(() => {
    if (mode === 'ai' && !gameOver) return 'You are X · AI is O';
    return null;
  }, [mode, gameOver]);

  const leader =
    scores.x > scores.o ? 'X' : scores.o > scores.x ? 'O' : null;

  const canUndo = past.length > 0 && !aiThinking;

  const boardWrapRef = useRef(null);
  const [movePanelLayout, setMovePanelLayout] = useState({
    boardPx: undefined,
  });

  useLayoutEffect(() => {
    const read = () => {
      const boardEl = boardWrapRef.current;
      const boardPx = boardEl ? Math.round(boardEl.getBoundingClientRect().height) : undefined;
      setMovePanelLayout({ boardPx });
    };

    read();
    const ro = new ResizeObserver(read);
    if (boardWrapRef.current) ro.observe(boardWrapRef.current);
    return () => ro.disconnect();
  }, [board, gameOver, currentPlayer, statusText, mode, moveLog.length, aiThinking]);

  const statusIsDraw = winner === 'draw';
  const resultAccent =
    winner === 'X' ? 'x' : winner === 'O' ? 'o' : null;

  return (
    <Box maxW={{ base: '100%', lg: '1080px' }} mx="auto" pb={{ base: 10, md: 14 }} px={{ base: 0, sm: 0 }}>
      {/* Page title */}
      <Flex align="flex-start" gap={{ base: 3, sm: 4 }} direction={{ base: 'column', sm: 'row' }} mb={{ base: 3, md: 4 }}>
        <Flex
          align="center"
          justify="center"
          w={{ base: '52px', sm: '56px' }}
          h={{ base: '52px', sm: '56px' }}
          borderRadius="2xl"
          bg="linear-gradient(145deg, #0f172a 0%, #1e293b 100%)"
          color="teal.300"
          flexShrink={0}
          boxShadow="0 10px 28px rgba(15, 23, 42, 0.22)"
        >
          <Icon as={MdGrid3X3} boxSize={{ base: 7, sm: 8 }} />
        </Flex>
        <Box flex={1} pt={{ sm: 0.5 }}>
          <Heading
            as="h1"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="800"
            letterSpacing="-0.04em"
            color="gray.900"
            lineHeight={1.05}
            mb={0}
          >
            Tic Tac Toe
          </Heading>
          <VStack align="stretch" spacing={0} mt={-0.5} mb={0} maxW="480px">
            <Text fontSize={{ base: 'sm', md: 'sm' }} color="gray.600" lineHeight="snug" fontWeight="500">
              Fast, clean, no friction.
            </Text>
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              color="gray.400"
              lineHeight="short"
              mt={-2}
              mb={0}
            >
              Play solo, vs AI, or pass-and-play — runs entirely in your browser.
            </Text>
          </VStack>
        </Box>
      </Flex>

      {/* 1. Top controls */}
      <TicTacToeControls
        mode={mode}
        onModeChange={handleModeChange}
        difficulty={difficulty}
        onDifficultyChange={handleDifficultyChange}
        soundEnabled={soundEnabled}
        onSoundToggle={handleSoundToggle}
        onReset={resetGame}
        onUndo={undo}
        canUndo={canUndo}
        difficultyDisabled={mode === 'pvp'}
        displayPanel={
          <AnimatePresence mode="wait">
            {!gameOver ? (
              <motion.div
                key="status-inplay"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
              >
                <Box
                  w="full"
                  maxW="full"
                  textAlign="center"
                  aria-live="polite"
                  aria-atomic="true"
                  px={2}
                >
                  <Text
                    as="div"
                    fontSize={
                      mode === 'pvp' || (mode === 'ai' && currentPlayer === HUMAN_PLAYER)
                        ? { base: 'md', md: 'lg' }
                        : 'sm'
                    }
                    fontWeight="700"
                    letterSpacing="-0.025em"
                    color="gray.800"
                    lineHeight={statusSubline ? 1 : 1.15}
                    noOfLines={1}
                  >
                    {statusText}
                  </Text>
                  {statusSubline ? (
                    <Text
                      as="div"
                      fontSize="10px"
                      color="gray.400"
                      mt="1px"
                      pt={0}
                      fontWeight="500"
                      letterSpacing="0.01em"
                      lineHeight={1}
                      noOfLines={1}
                    >
                      {statusSubline}
                    </Text>
                  ) : null}
                </Box>
              </motion.div>
            ) : (
              <motion.div
                key="game-result-display"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
              >
                <Box
                  w="full"
                  maxW="full"
                  px={2}
                  textAlign="center"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <Text
                    as="div"
                    fontSize={{ base: 'md', md: 'lg' }}
                    fontWeight="800"
                    letterSpacing="-0.03em"
                    lineHeight={1.05}
                    textAlign="center"
                    noOfLines={1}
                    color={
                      statusIsDraw
                        ? 'gray.700'
                        : resultAccent === 'x'
                          ? 'cyan.800'
                          : 'orange.800'
                    }
                  >
                    {statusText}
                  </Text>
                  <Text
                    as="div"
                    fontSize="10px"
                    color="gray.500"
                    fontWeight="600"
                    mt="4px"
                    pt={0}
                    lineHeight={1}
                    textAlign="center"
                    noOfLines={1}
                    fontVariantNumeric="tabular-nums"
                  >
                    X {scores.x} · Draws {scores.draws} · O {scores.o}
                  </Text>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        }
      />

      {/* 2. Main game: status, then scores | board | move log */}
      <Box
        as="section"
        aria-labelledby="ttt-game-heading"
        mt={{ base: 4, md: 5 }}
        mb={{ base: 4, md: 6 }}
      >
        <Box as="span" id="ttt-game-heading" srOnly>
          Current game
        </Box>

        <VStack spacing={{ base: 4, lg: 5 }} align="stretch">
          <Box
            borderRadius={{ base: '2xl', lg: '3xl' }}
            bg={{ base: 'transparent', lg: 'rgba(248, 250, 252, 0.65)' }}
            borderWidth={{ base: 0, lg: '1px' }}
            borderColor={{ base: 'transparent', lg: 'gray.100' }}
            boxShadow={{ base: 'none', lg: '0 1px 3px rgba(15, 23, 42, 0.04), inset 0 1px 0 rgba(255,255,255,0.8)' }}
            p={{ base: 0, lg: 5 }}
            mx={{ lg: -2 }}
          >
            <Flex
              as="section"
              aria-label="Session scores, game board, and move log"
              direction={{ base: 'column', lg: 'row' }}
              align={{ base: 'stretch', lg: 'flex-start' }}
              justify="center"
              gap={{ base: 6, lg: 6 }}
              wrap="nowrap"
            >
              <Box
                flex={{ lg: '0 0 224px' }}
                w={{ base: '100%', lg: 'auto' }}
                maxW={{ base: '100%', lg: '248px' }}
                mx={{ base: 'auto', lg: 0 }}
                alignSelf="flex-start"
                h={{
                  base: 'auto',
                  lg: movePanelLayout.boardPx ? `${movePanelLayout.boardPx}px` : 'auto',
                }}
                minH={{ base: 'auto', lg: movePanelLayout.boardPx ? `${movePanelLayout.boardPx}px` : undefined }}
                display="flex"
                flexDirection="column"
              >
                <TicTacToeScoreboard x={scores.x} o={scores.o} draws={scores.draws} leader={leader} fillParent />
              </Box>

              <VStack
                flex={{ lg: '1 1 auto' }}
                minW={0}
                spacing={{ base: 2, md: 3 }}
                align="center"
                maxW={{ base: '100%', lg: 'none' }}
              >
                <Box ref={boardWrapRef} w="full" maxW={{ base: 'min(100%, 400px)', sm: '440px' }} mx="auto">
                  <TicTacToeBoard
                    board={board}
                    winningLine={winningLine}
                    gameOver={gameOver}
                    currentPlayer={currentPlayer}
                    onMove={onCellClick}
                  />
                </Box>

                <Box w="full" maxW={{ base: 'min(100%, 400px)', sm: '440px' }} mx="auto" px={{ base: 1, sm: 0 }}>
                  <AnimatePresence>
                    {gameOver ? (
                      <motion.div
                        key="game-result-cta"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                        style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                      >
                        <Button
                          mt={4}
                          mb={1}
                          size="lg"
                          colorScheme="teal"
                          borderRadius="xl"
                          fontWeight="700"
                          px={10}
                          h="48px"
                          fontSize="md"
                          boxShadow="0 2px 8px -2px rgba(13, 148, 136, 0.4), 0 8px 24px -8px rgba(13, 148, 136, 0.35)"
                          _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px -2px rgba(13, 148, 136, 0.45), 0 12px 28px -8px rgba(13, 148, 136, 0.3)',
                          }}
                          _active={{ transform: 'translateY(0)' }}
                          transition="transform 0.14s ease, box-shadow 0.14s ease"
                          onClick={resetGame}
                        >
                          Play again
                        </Button>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </Box>
              </VStack>

              <Box
                flex={{ lg: '0 0 264px' }}
                w={{ base: '100%', lg: 'auto' }}
                maxW={{ base: '100%', lg: '288px' }}
                mx={{ base: 'auto', lg: 0 }}
                alignSelf="flex-start"
                h={{
                  base: 'auto',
                  lg: movePanelLayout.boardPx ? `${movePanelLayout.boardPx}px` : 'auto',
                }}
                minH={{ base: 'auto', lg: movePanelLayout.boardPx ? `${movePanelLayout.boardPx}px` : undefined }}
                display="flex"
                flexDirection="column"
              >
                <TicTacToeMoveHistory moves={moveLog} fillParent />
              </Box>
            </Flex>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default TicTacToe;
